import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  payrollApi,
  type CompanyEmployeeRosterRow,
} from "@/services/payroll";

export type CompanyEmployeeSearchOption = {
  employee: CompanyEmployeeRosterRow;
  employeeId: string;
  displayName: string;
  customEmployeeId: string | null;
  employeeNumberLabel: string;
  primaryLabel: string;
  secondaryLabel: string;
  searchLabel: string;
};

type UseCompanyEmployeeSearchResult = {
  employees: CompanyEmployeeRosterRow[];
  options: CompanyEmployeeSearchOption[];
  filtered: CompanyEmployeeRosterRow[];
  filteredOptions: CompanyEmployeeSearchOption[];
  loading: boolean;
  error: string | null;
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  submittedQuery: string;
  submitSearch: () => void;
  clearSearch: () => void;
  hasActiveQuery: boolean;
  resultCount: number;
  refresh: () => Promise<void>;
};

function normalizeSearchText(value: string | null | undefined): string {
  return (value || "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function composeEmployeeDisplayName(row: CompanyEmployeeRosterRow): string {
  const structured = [
    row.first_name?.trim() || "",
    row.middle_names?.trim() || "",
    row.last_name?.trim() || "",
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return structured || row.full_name?.trim() || "Employee";
}

function formatEmployeeNumberLabel(
  customEmployeeId: string | null | undefined
): string {
  return customEmployeeId && customEmployeeId.trim()
    ? `Emp # ${customEmployeeId.trim()}`
    : "Emp ID not assigned";
}

function toSearchOption(
  employee: CompanyEmployeeRosterRow
): CompanyEmployeeSearchOption {
  const customEmployeeId =
    employee.custom_employee_id == null
      ? null
      : String(employee.custom_employee_id);

  const displayName =
    composeEmployeeDisplayName(employee);

  const employeeNumberLabel =
    formatEmployeeNumberLabel(customEmployeeId);

  return {
    employee,
    employeeId: String(employee.id),
    displayName,
    customEmployeeId,
    employeeNumberLabel,
    primaryLabel: displayName,
    secondaryLabel: employeeNumberLabel,
    searchLabel: `${employeeNumberLabel} — ${displayName}`,
  };
}

export function useCompanyEmployeeSearch(
  companyId: string | null | undefined
): UseCompanyEmployeeSearchResult {
  const { initializing, isAuthenticated } = useAuth();

  const canLoadEmployees =
    Boolean(companyId) && !initializing && isAuthenticated;
  const [employees, setEmployees] = useState<CompanyEmployeeRosterRow[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");

  const loadEmployees = useCallback(async () => {
    if (!canLoadEmployees || !companyId) {
      setEmployees([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const rows = await payrollApi.listCompanyEmployeeRoster(companyId);
      setEmployees(rows ?? []);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load employees.";

      setEmployees([]);
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [canLoadEmployees, companyId]);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const options = useMemo(
    () => employees.map(toSearchOption),
    [employees]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery =
      normalizeSearchText(query);

    if (!normalizedQuery) {
      return options;
    }

    return options.filter((option) =>
      normalizeSearchText(option.searchLabel).includes(normalizedQuery)
    );
  }, [options, query]);

  const filtered = useMemo(
    () => filteredOptions.map((option) => option.employee),
    [filteredOptions]
  );

  const submitSearch = useCallback(() => {
    setSubmittedQuery(query);
  }, [query]);

  const clearSearch = useCallback(() => {
    setQuery("");
    setSubmittedQuery("");
  }, []);

  const hasActiveQuery =
    normalizeSearchText(query).length > 0;

  return {
    employees,
    options,
    filtered,
    filteredOptions,
    loading,
    error,
    query,
    setQuery,
    submittedQuery,
    submitSearch,
    clearSearch,
    hasActiveQuery,
    resultCount: filteredOptions.length,
    refresh: loadEmployees,
  };
}